import React, { useEffect, useId, useState } from "react";
import { useConfig } from "config/useConfig";
import { getPalette } from "config/colorUtils";
import { usePanelContainer } from "@features/analysis/rendering/Panels/usePanelContainer";
import { getDisplayRuntimeProfile } from "config/displayRuntime";

function getStopsFromPalette(
  palette: [number, number, number, number][],
): { offset: string; color: string }[] {
  return palette.map((color, idx) => {
    const t = idx / (palette.length - 1);
    const [r, g, b] = color;
    return {
      offset: `${(t * 100).toFixed(2)}%`,
      color: `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`,
    };
  });
}

function clampNumber(value: number, min: number, max: number) {
  const lower = Math.min(min, max);
  const upper = Math.max(min, max);
  return Math.max(lower, Math.min(upper, value));
}

function useElementSize(ref: React.RefObject<HTMLDivElement>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setSize((prev) => {
        const width = Math.round(rect.width * 10) / 10;
        const height = Math.round(rect.height * 10) / 10;
        if (prev.width === width && prev.height === height) return prev;
        return { width, height };
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [ref]);

  return size;
}

export default function SvgColorScaleBar({
  textColor = "white",
  fontWeight = 500,
  min = 0,
  max = 5.5,
  label = "",
  variant = "panel",
  displayValue,
  svgRef,
  containerRef,
}: {
  textColor?: string;
  fontWeight?: number;
  min?: number;
  max?: number;
  label?: string;
  variant?: "panel" | "modal";
  svgRef: React.RefObject<SVGSVGElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  displayValue?: number;
}) {
  const { config } = useConfig();
  const { analysisType } = usePanelContainer();
  const { typographyScale, uiScale } = getDisplayRuntimeProfile();
  const observedSize = useElementSize(containerRef);

  const gradId = useId().replace(/[:]/g, "");
  if (!containerRef?.current) return null;

  const fallbackRect = containerRef.current.getBoundingClientRect();
  const dims = {
    width: observedSize.width || fallbackRect.width,
    height: observedSize.height || fallbackRect.height,
  };
  if (dims.width <= 0 || dims.height <= 0) return null;

  const keyName =
    analysisType === "wallthickness" ? "paletteLAWT" : "paletteFFR";
  const stops = getStopsFromPalette(getPalette(keyName, config.colors));
  const isFfr = analysisType === "FFR";

  const tickFontSize =
    variant === "modal"
      ? clampNumber(
          12 * typographyScale,
          Math.min(dims.width * 0.24, dims.height * 0.032),
          18 * typographyScale,
        )
      : clampNumber(
          10 * typographyScale,
          Math.min(dims.width * 0.075, dims.height * 0.022),
          18 * typographyScale,
        );
  const valueFontSize = (variant === "modal" ? 13 : 12) * typographyScale;
  const barWidth = isFfr
    ? clampNumber(dims.width * 0.08, 18 * uiScale, 28 * uiScale)
    : variant === "modal"
      ? clampNumber(dims.width * 0.18, 12 * uiScale, 20 * uiScale)
      : clampNumber(dims.width * 0.07, 16 * uiScale, 26 * uiScale);
  const labelGap = clampNumber(dims.height * 0.012, 6 * uiScale, 12 * uiScale);
  const labelSafeTop = tickFontSize + labelGap;
  const labelSafeBottom = tickFontSize + labelGap;

  const ffrOrientationSize = Math.max(100, Math.min(300, dims.height * 0.15));
  const panelBottomReserve = isFfr
    ? ffrOrientationSize + labelSafeBottom
    : clampNumber(
        dims.height * 0.24,
        96 * typographyScale,
        190 * typographyScale,
      );

  const layout = (() => {
    if (variant === "modal") {
      const edgeGap = clampNumber(dims.width * 0.04, 2 * uiScale, 6 * uiScale);
      const rightLimit = Math.max(edgeGap, dims.width - barWidth - edgeGap);
      const preferredX = dims.width * 0.52;
      const x = clampNumber(preferredX, edgeGap, rightLimit);
      const top = labelSafeTop;
      const height = Math.max(80, dims.height - labelSafeTop - labelSafeBottom);
      return { barX: x, barTopY: top, barHeight: height };
    }

    if (isFfr) {
      const height = clampNumber(
        dims.height * 0.5,
        180 * uiScale,
        320 * uiScale,
      );
      const x = Math.max(0, dims.width - 60 * uiScale - barWidth);
      const top = Math.max(
        labelSafeTop,
        dims.height - height - panelBottomReserve,
      );
      return { barX: x, barTopY: top, barHeight: height };
    }

    const top = clampNumber(42 * uiScale, dims.height * 0.065, 72 * uiScale);
    const availableHeight = dims.height - top - panelBottomReserve;
    const height = Math.max(90 * uiScale, availableHeight);
    const minCenterX = tickFontSize * 1.25;
    const preferredX = clampNumber(
      dims.width * 0.085,
      16 * uiScale,
      40 * uiScale,
    );
    const valueLabelReserve = valueFontSize * 2.4 + 6 * uiScale;
    const x = Math.max(preferredX, minCenterX - barWidth / 2, valueLabelReserve);
    return { barX: x, barTopY: top, barHeight: height };
  })();

  const barX = layout.barX;
  const barTopY = layout.barTopY;
  const barHeight = Math.min(
    layout.barHeight,
    Math.max(0, dims.height - barTopY - labelSafeBottom),
  );
  const labelCenterX = barX + barWidth / 2;

  const ffrTickValues = isFfr
    ? Array.from({ length: Math.round((max - min) / 0.1) + 1 }, (_, index) =>
        Number((max - index * 0.1).toFixed(1)),
      )
    : [];
  let valueY: number | undefined = undefined;
  if (
    displayValue !== undefined &&
    displayValue >= min &&
    displayValue <= max
  ) {
    const t = (displayValue - min) / (max - min);
    valueY = barTopY + barHeight - t * barHeight;
  }

  function Text({
    x,
    y,
    name,
    fontSize,
    fontWeight: fw,
    textAnchor = "end",
  }: {
    x: number;
    y: number;
    name: string;
    fontSize?: number;
    fontWeight?: number;
    textAnchor?: "start" | "middle" | "end";
  }) {
    return (
      <text
        x={x}
        y={y}
        fontSize={fontSize ?? valueFontSize}
        fill={textColor}
        textAnchor={textAnchor}
        alignmentBaseline="middle"
        fontWeight={fw ?? fontWeight}
      >
        {name}
      </text>
    );
  }

  return (
    <svg
      className="select-none"
      ref={svgRef}
      width={dims.width}
      height={dims.height}
      viewBox={`0 0 ${dims.width} ${dims.height}`}
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 5,
        overflow: "visible",
      }}
    >
      <defs>
        <linearGradient
          id={`colorScale-${gradId}`}
          x1="0%"
          y1="100%"
          x2="0%"
          y2="0%"
        >
          {stops.map((s, i) => (
            <stop key={i} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
      </defs>
      <rect
        x={barX}
        y={barTopY}
        width={barWidth}
        height={barHeight}
        fill={`url(#colorScale-${gradId})`}
        rx={Math.min(8 * uiScale, barWidth / 2)}
      />
      {isFfr && (
        <Text
          x={barX}
          y={Math.max(tickFontSize, barTopY - tickFontSize * 1.25)}
          fontSize={20 * typographyScale}
          textAnchor="start"
          name="FFR"
        />
      )}
      {isFfr ? (
        ffrTickValues.map((tickValue) => {
          const t = (tickValue - min) / (max - min);
          const tickY = barTopY + barHeight - t * barHeight;
          return (
            <Text
              key={`ffr-tick-${tickValue.toFixed(1)}`}
              x={barX + barWidth + 8 * uiScale}
              y={tickY}
              textAnchor="start"
              name={tickValue.toFixed(1)}
            />
          );
        })
      ) : (
        <>
          <Text
            x={labelCenterX}
            y={Math.max(tickFontSize / 2, barTopY - labelGap)}
            fontSize={tickFontSize}
            textAnchor="middle"
            name={max.toFixed(1)}
          />
          <Text
            x={labelCenterX}
            y={Math.min(
              dims.height - tickFontSize / 2,
              barTopY + barHeight + labelGap,
            )}
            fontSize={tickFontSize}
            textAnchor="middle"
            name={min.toFixed(1)}
          />
        </>
      )}
      {label && (
        <text
          x={barX + barWidth + 20 * uiScale}
          y={barTopY + barHeight / 2 + 3}
          fontSize={valueFontSize}
          fill={textColor}
          fontWeight={fontWeight}
          textAnchor="middle"
          alignmentBaseline="middle"
          transform={`rotate(-90, ${barX + barWidth + 18 * uiScale}, ${barTopY + barHeight / 2})`}
          style={{ letterSpacing: "1px" }}
        >
          {label}
        </text>
      )}
      {valueY !== undefined && displayValue !== min && displayValue !== max && (
        <>
          <line
            x1={barX}
            x2={barX + barWidth}
            y1={valueY}
            y2={valueY}
            stroke={textColor}
            strokeWidth={2}
            strokeDasharray={`${Math.min(7, barWidth / 3)} ${Math.max(0, barWidth - 14)} ${Math.min(7, barWidth / 3)}`}
          />
          <Text
            x={
              variant === "modal"
                ? Math.max(valueFontSize * 0.6, barX - 6 * uiScale)
                : Math.max(valueFontSize * 2.6, barX - 10 * uiScale)
            }
            y={valueY}
            fontSize={valueFontSize}
            fontWeight={700}
            name={displayValue!.toFixed(1)}
          />
        </>
      )}
    </svg>
  );
}
